import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

/**
 * Searchable multi-select with chips. Allows free-text custom additions.
 * `value` is an array of strings. Stable across the app.
 */
export function MultiTagSelect({
  value,
  onChange,
  options,
  placeholder = "Select…",
  allowCustom = true,
  className,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  options: string[];
  placeholder?: string;
  allowCustom?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((s) => s.toLowerCase().includes(q)) : options;
  }, [query, options]);

  const toggle = (item: string) => {
    const exists = value.some((v) => v.toLowerCase() === item.toLowerCase());
    onChange(exists ? value.filter((v) => v.toLowerCase() !== item.toLowerCase()) : [...value, item]);
  };

  const remove = (item: string) =>
    onChange(value.filter((v) => v.toLowerCase() !== item.toLowerCase()));

  const showCustom =
    allowCustom &&
    query.trim().length > 0 &&
    !options.some((s) => s.toLowerCase() === query.trim().toLowerCase()) &&
    !value.some((s) => s.toLowerCase() === query.trim().toLowerCase());

  return (
    <div className={cn("space-y-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal h-auto min-h-9 py-1.5"
          >
            <div className="flex flex-wrap gap-1 items-center">
              {value.length === 0 ? (
                <span className="text-muted-foreground">{placeholder}</span>
              ) : (
                value.map((v) => (
                  <span
                    key={v}
                    className="inline-flex items-center gap-1 bg-primary/10 text-primary border border-primary/20 px-1.5 py-0.5 text-xs rounded-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(v);
                    }}
                  >
                    {v}
                    <X className="h-3 w-3" />
                  </span>
                ))
              )}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search or add…"
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              <CommandEmpty>No matches.</CommandEmpty>
              <CommandGroup>
                {items.map((s) => {
                  const checked = value.some((v) => v.toLowerCase() === s.toLowerCase());
                  return (
                    <CommandItem
                      key={s}
                      value={s}
                      onSelect={() => toggle(s)}
                    >
                      <Check className={cn("mr-2 h-4 w-4", checked ? "opacity-100" : "opacity-0")} />
                      {s}
                    </CommandItem>
                  );
                })}
                {showCustom && (
                  <CommandItem
                    value={`__add_${query}`}
                    onSelect={() => {
                      onChange([...value, query.trim()]);
                      setQuery("");
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add “{query.trim()}”
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
